/**
 * MasterDataExportTerminalFormDialog — Create/Edit `d_export_terminal`.
 * Compact Sales Contract style dialog. Single-column.
 *
 * PK `id` là AUTO-INT (DB-generated): create KHÔNG có field id;
 * edit hiện `id` readonly. Update dùng { id, body }.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdExportTerminalMutation,
  useUpdateMdExportTerminalMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataExportTerminal } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataExportTerminal | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  export_terminal: string
}

const EMPTY: FormState = {
  export_terminal: '',
}

export default function MasterDataExportTerminalFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createExportTerminal, { isLoading: creating }] = useCreateMdExportTerminalMutation()
  const [updateExportTerminal, { isLoading: updating }] = useUpdateMdExportTerminalMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      setForm({
        export_terminal: initialValue.export_terminal ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const canSave = !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    export_terminal: form.export_terminal.trim() || null,
  })

  const handleSave = async () => {
    try {
      if (isEdit && initialValue) {
        await updateExportTerminal({ id: initialValue.id, body: buildBody() }).unwrap()
        snack.success('Export terminal updated')
      } else {
        await createExportTerminal(buildBody()).unwrap()
        snack.success('Export terminal created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Export Terminal' : 'New Export Terminal'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {isEdit && initialValue ? (
            <Grid item xs={12}>
              <TextField
                label="ID"
                size="small"
                fullWidth
                value={initialValue.id}
                InputLabelProps={{ shrink: true }}
                inputProps={{ readOnly: true, tabIndex: -1 }}
                helperText="ID is auto-generated."
                sx={roSx}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label="Export Terminal"
              size="small"
              fullWidth
              value={form.export_terminal}
              onChange={(e) => set('export_terminal', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
